import { useMatomo } from '@datapunt/matomo-tracker-react';
import { Box, ButtonGroup, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, useTheme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import DialogContentText from '@material-ui/core/DialogContentText';
import Paper from '@material-ui/core/Paper';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ApplicationContainer, CustomTable } from './applications-list.styles';
import CustomButton from '../../../shared/components/custom-button/custom-button.component';
import useUserStatus from '../../../utils/use-user-status';
import {
  applicationCreate,
  deleteApplicationAsync,
  fetchApplicationAsync,
  fetchApplicationReopenAsync,
  setStep,
  setStepsCompleted
} from '../../redux/scholarship.actions';
import { MAppMeta } from '../../redux/scholarship.models';
import {
  selectApplicationsCompleted,
  selectApplicationsInProgress,
  selectLoadingApplications,
  selectProfilesParent
} from '../../redux/scholarship.selectors';

const ApplicationsList = ({
  dispatchFetchApplicationAsync,
  dispatchSetStep,
  dispatchSetStepCompleted,
  dispatchApplicationCreate,
  dispatchDeleteApplicationAsync,
  dispatchApplicationReopenAsync
}) => {
  const isFetching = useSelector(selectLoadingApplications);
  const applicationsInProgress = useSelector(selectApplicationsInProgress as (state: unknown) => MAppMeta[]);
  const applicationsCompleted = useSelector(selectApplicationsCompleted as (state: unknown) => MAppMeta[]);
  const profilesParent = useSelector(selectProfilesParent);
  useTheme();
  const navigate = useNavigate();
  const userStatus = useUserStatus();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openAppDialog, setOpenAppDialog] = useState<boolean>(false);
  const [isResumingApplication, setIsResumingApplication] = useState<boolean>(false);
  const [confirmation, setConfirmation] = useState(null);
  const [appId, setAppId] = useState<string>('');

  const [reopenId, setReopenId] = useState<string>('');
  const { trackPageView } = useMatomo();

  // Track page view
  useEffect(() => {
    trackPageView({});
  }, []);

  const useStyles = makeStyles((theme) =>
    createStyles({
      title: {
        fontWeight: 800
      },
      buttonSmall: {
        marginRight: theme.spacing(1),
        color: 'black',
        backgroundColor: '#e3e7ea',
        minWidth: '170px',
        width: 'auto',
        height: '30px',
        letterSpacing: '0.5px',
        lineHeight: '30px',
        padding: '0',
        fontSize: '12px',
        fontWeight: 'bold'
      },
      hideOnMobile: {
        [theme.breakpoints.only('xs')]: {
          display: 'none'
        }
      },
      showOnlyOnMobile: {
        display: 'none',
        [theme.breakpoints.only('xs')]: {
          display: 'inherit'
        }
      }
    })
  );
  const classes = useStyles();

  const handleToggleDialog = (status: boolean) => {
    setOpenDialog(status);
  };

  const handleAppDialogOpen = (id: string) => {
    setAppId(id);
    setOpenAppDialog(true);
  };

  const handleAppDialogCancel = () => {
    setOpenAppDialog(false);
  };

  const handleConfirmType = (e) => {
    setConfirmation(e.target.value.toLowerCase());
  };

  const handleAppDialogDelete = () => {
    if (confirmation === 'permanently delete') {
      setOpenAppDialog(false);
      dispatchDeleteApplicationAsync(appId);
    }
  };

  const handleNewApplication = () => {
    console.log('logging applications in progress');
    console.log(applicationsInProgress);
    dispatchApplicationCreate(userStatus ? userStatus['attributes']['sub'] : undefined, profilesParent || []);
    dispatchSetStepCompleted({});
    dispatchSetStep(undefined, 0);
    navigate('/applications/new');
  };

  const handleResumeApplication = (id: string) => {
    setIsResumingApplication(true);

    dispatchFetchApplicationAsync(id).then((result) => {
      setIsResumingApplication(false);

      if (result) {
        navigate(`/applications/${id}`);
        dispatchSetStep(undefined, 0);
      }
    });
  };

  const handleReopenApplication = (id: string) => {
    handleToggleDialog(false);
    dispatchApplicationReopenAsync(id).then(() => {
      handleResumeApplication(id);
    });
  };

  const handleViewCompleteApplication = (id: string) => {
    navigate(`/applications/complete/${id}`);
  };

  const formatYear = (awardYear: number): string => {
    const applicationYear = awardYear - 1;

    return `${applicationYear}-${awardYear.toString().slice(-2)}`;
  };

  return (
    <ApplicationContainer>
      <Grid container justifyContent="space-between">
        <Grid item>
          <Typography variant="h4">Scholarship Applications</Typography>
        </Grid>
        <Grid item>
          <CustomButton onClick={() => handleNewApplication()}>Start New Application</CustomButton>
        </Grid>
      </Grid>
      <LoadingOverlay active={isFetching} spinner text="Loading scholarship applications...">
        <Box pt={3}>
          <Typography variant="h6">Applications in Progress</Typography>
          <LoadingOverlay active={!isFetching && isResumingApplication} spinner text="Resuming application...">
            <TableContainer component={Paper}>
              <CustomTable>
                <colgroup>
                  <col width="35%" />
                  <col width="30%" />
                  <col width="35%" />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.title}>Student Name</TableCell>
                    <TableCell className={classes.title} align="center">
                      Year
                    </TableCell>
                    <TableCell className={classes.title} align="center">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicationsInProgress.map((row: MAppMeta) => (
                    <TableRow key={row.id}>
                      <TableCell style={{ padding: 10 }} component="th" scope="row">
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell style={{ padding: 0 }} align="center">
                        {formatYear(row.year)}
                      </TableCell>
                      <TableCell style={{ padding: 10 }} align="center">
                        <Button variant="contained" onClick={() => handleResumeApplication(row.id)} size="small">
                          <span className={classes.hideOnMobile}>Continue</span>
                          <span className={classes.showOnlyOnMobile}>Edit</span>
                        </Button>
                        <Button style={{ margin: 10 }} variant="contained" onClick={() => handleAppDialogOpen(row.id)} size="small">
                          <span>Delete</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </CustomTable>
            </TableContainer>
          </LoadingOverlay>
        </Box>
        <Box pt={4}>
          <Typography variant="h6">Submitted Applications </Typography>
          <TableContainer component={Paper}>
            <CustomTable aria-label="simple table">
              <colgroup>
                <col width="35%" />
                <col width="30%" />
                <col width="35%" />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.title}>Student Name</TableCell>
                  <TableCell className={classes.title} align="center">
                    Year
                  </TableCell>
                  <TableCell className={classes.title} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applicationsCompleted.map((row: MAppMeta) => (
                  <TableRow key={row.id}>
                    <TableCell style={{ padding: 10 }} component="th" scope="row">
                      {row.firstName} {row.lastName}
                    </TableCell>
                    <TableCell style={{ padding: 0 }} align="center">
                      {formatYear(row.year)}
                    </TableCell>
                    <TableCell style={{ padding: 10 }} align="center">
                      <ButtonGroup variant="contained" size="small">
                        <Button variant="contained" onClick={() => handleViewCompleteApplication(row.id)}>
                          View
                        </Button>
                        <Button
                          variant="contained"
                          disabled={dayjs().get('year') !== row.year - 1}
                          onClick={() => {
                            setReopenId(row.id);
                            handleToggleDialog(true);
                          }}
                        >
                          <span className={classes.hideOnMobile}>Make Additional Changes</span>
                          <span className={classes.showOnlyOnMobile}>Re-Open</span>
                        </Button>
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </CustomTable>
          </TableContainer>
        </Box>
      </LoadingOverlay>
      <Dialog open={openDialog} onClose={() => handleToggleDialog(false)}>
        <DialogTitle id="delete-dialog-title">Update Application?</DialogTitle>
        <DialogContent>After re-opening an application remember to re-submit before the Dec. 31 deadline.</DialogContent>
        <DialogActions>
          <Button variant="contained" autoFocus onClick={() => handleToggleDialog(false)} color="primary">
            Back
          </Button>
          <Button onClick={() => handleReopenApplication(reopenId)} color="secondary" variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* dialog for deleting apps*/}

      <Dialog open={openAppDialog} onClose={handleAppDialogCancel} aria-labelledby="form-dialog-app">
        <DialogTitle id="form-dialog-app">Are you sure?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To delete this application, type in the word <b>permanently delete</b> then click Submit.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="confirm"
            label="type 'permanently delete' to confirm this action"
            type="text"
            fullWidth
            onChange={handleConfirmType}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAppDialogCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAppDialogDelete} color="primary">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </ApplicationContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  dispatchApplicationCreate: (userId, parents) => dispatch(applicationCreate(userId, parents)),
  dispatchSetStep: (oldStep, newStep) => dispatch(setStep(oldStep, newStep)),
  dispatchSetStepCompleted: (value) => dispatch(setStepsCompleted(value)),
  dispatchFetchApplicationAsync: (id) => dispatch(fetchApplicationAsync(id)),
  dispatchDeleteApplicationAsync: (id) => dispatch(deleteApplicationAsync(id)),
  dispatchApplicationReopenAsync: (id) => dispatch(fetchApplicationReopenAsync(id))
});
export default connect(null, mapDispatchToProps)(ApplicationsList);
